import { useState, useRef, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Home,
  Users,
  Shield,
  User,
  Menu,
  X,
  LogOut,
  Settings,
  Video,
  Monitor,
  Play,
  Zap,
  Clock,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";

const Layout = () => {
  const { user, logout, hasPermission, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // If navigating away from /camera-monitoring, stop all streams
    if (
      prevPathRef.current === "/camera-monitoring" &&
      location.pathname !== "/camera-monitoring"
    ) {
      fetch(`${import.meta.env.VITE_API_URL}/cameras/restart-streams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add authorization header if needed
        },
      }).catch((err) => {
        console.error("Failed to stop all camera streams:", err);
      });
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  // Debug logging
  console.log('Layout navigation debug:', {
    user: user?.username,
    userRole: user?.role?.name,
    isSuperAdmin: isSuperAdmin(),
    hasCameraPermission: hasPermission("cameras", "read")
  })

  const navigation = [
    ...(hasPermission("cameras", "read")
      ? [
        {
          name: "Camera Monitoring",
          href: "/camera-monitoring",
          icon: Monitor,
        },
      ]
      : []),
    ...(isSuperAdmin()
      ? [{ name: "Cameras", href: "/dashboard", icon: Home }]
      : []),

    ...(hasPermission("users", "read")
      ? [{ name: "Users", href: "/users", icon: Users }]
      : []),


    ...(hasPermission("roles", "read")
      ? [{ name: "Roles", href: "/roles", icon: Shield }]
      : []),
    // ...(hasPermission("cameras", "read")
    //   ? [{ name: "Cameras List", href: "/cameras", icon: Video }]
    //   : []),

    { name: "Profile", href: "/profile", icon: User },
  ];

  const isActive = (href) => location.pathname === href;

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? "block" : "hidden"
          }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-gradient-to-b from-indigo-700 via-indigo-800 to-cyan-700">
          <div className="flex items-center justify-between px-4 py-4 border-b border-indigo-600/30">
            <img 
              src="/assets/sharevision-logo.svg" 
              alt="ShareVision" 
              className="h-10 w-full max-w-[180px] object-contain" 
            />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-cyan-100 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${isActive(item.href)
                  ? "bg-cyan-600 text-white"
                  : "text-cyan-100 hover:bg-indigo-600 hover:text-white"
                  }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>


      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className="flex flex-col flex-grow bg-gradient-to-b from-indigo-700 via-indigo-800 to-cyan-700">
          <div className={`flex shrink-0 border-b border-indigo-600/30 ${sidebarCollapsed ? 'h-16 flex-col justify-center items-center' : 'flex-col py-4 gap-2'}`}>
            {!sidebarCollapsed && (
              <div className="flex items-center justify-center px-4">
                <img 
                  src="/assets/sharevision-logo.svg" 
                  alt="ShareVision" 
                  className="h-10 w-full max-w-[200px] object-contain" 
                />
              </div>
            )}
            <div className={`flex justify-center ${!sidebarCollapsed && 'px-2'}`}>
              <button
                onClick={toggleSidebar}
                className="text-cyan-100 hover:text-white transition-colors p-1"
                title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              >
                {sidebarCollapsed ? (
                  <PanelLeftOpen className="h-5 w-5" />
                ) : (
                  <PanelLeftClose className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${isActive(item.href)
                  ? "bg-cyan-600 text-white"
                  : "text-cyan-100 hover:bg-indigo-600 hover:text-white"
                  }`}
                title={sidebarCollapsed ? item.name : ""}
              >
                <item.icon className={`${sidebarCollapsed ? 'mx-auto' : 'mr-3'} h-5 w-5`} />
                {!sidebarCollapsed && item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 via-cyan-50 to-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-indigo-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          {/* Mobile user info and logout */}
          <div className="flex items-center gap-x-2 lg:hidden flex-1 justify-end">
            <div className="flex items-center gap-x-1">
              <span className="text-xs text-gray-700 truncate max-w-24">
                {user?.username}
              </span>
              {isSuperAdmin() && (
                <span className="inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">
                  Admin
                </span>
              )}
            </div>
            <button
              onClick={logout}
              className="flex items-center text-sm text-gray-700 hover:text-gray-900"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>

          {/* Desktop user info and logout */}
          <div className="hidden lg:flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="flex items-center gap-x-2">
                <span className="text-sm text-gray-700">
                  Welcome, {user?.username}
                </span>
                {isSuperAdmin() && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                    Super Admin
                  </span>
                )}
              </div>
              <div className="h-6 w-px bg-gray-200" />
              <button
                onClick={logout}
                className="flex items-center gap-x-2 text-sm text-gray-700 hover:text-gray-900"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-1">
          <div className="">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
